import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { TABLE_RECORD_PER_PAGE } from "../../../constants";
import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import {
  loaderPath,
  riskLevelDefaultValues,
  riskLevelFields,
  riskLevelTableColumns,
} from "../../../descriptions/settings.descriptions";
import useAPI from "../../../hooks/useAPI";
import useI18N from "../../../hooks/useI18N";
import usePagination from "../../../hooks/usePagination";
import { addRiskLevel } from "../../../redux/slices/settings.slices";
import { Edit } from "../../../shared/icon";
import MUIModal from "../../../shared/MUIModal";
import SABasicTable from "../../../shared/SABasicTable";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SACard from "../../../shared/SACard";
import SAForm from "../../../shared/SAForm";
import SAFormFields from "../../../shared/SAFormFields";
import SAStack from "../../../shared/SAStack";
import SAStatusBadge from "../../../shared/SAStatusBadge";
import SATypography from "../../../shared/SATypography";
import showToast from "../../../utils/toast";

function formatNumber(num) {
  // Coerce input to a number first so we can safely call Number methods.
  if (num == null) return num;
  const n = Number(num);
  if (Number.isNaN(n)) return num;

  // fractional digits or only up to 2.
  const rawStr = typeof num === "string" ? num : String(n);
  const decimalPart = (rawStr.split(".")[1] || "").replace(/0+$/g, "");

  if (!decimalPart || decimalPart.length <= 2) {
    return n.toFixed(2); // Ensure two decimals (string with trailing zeros)
  }

  const decimals = Math.min(decimalPart.length, 4);
  return n.toFixed(decimals);
}

const primaryActionStyle = {
  size: "small",
  variant: "outlined",
  sx: { "& svg": { ml: 1 } },
};

const RiskLevel = () => {
  const { t } = useI18N();
  const { api } = useAPI();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalDefaultValues, setModalDefaultValues] = useState({});

  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const riskLevelList = useSelector((state) => state.settings?.riskLevel);
  const { count, results: riskLevelData } = riskLevelList || {};

  const { register, handleSubmit, control, formState, reset } = useForm({
    defaultValues: modalDefaultValues,
    mode: "onChange",
  });
  const { isDirty } = formState;

  const {
    totalPage,
    currentPage,
    itemPerPage,
    setCurrentPage,
    setItemPerPage,
  } = usePagination(count, TABLE_RECORD_PER_PAGE);

  const loadingState = useSelector((state) => ({
    addRiskLevel: state.loader?.[loaderPath.addRiskLevel],
    getRiskLevelStatus: state.loader?.[loaderPath.getRiskLevelStatus],
  }));

  useEffect(() => {
    callRiskLevelAPI();
  }, [currentPage, itemPerPage]);

  const getRiskLevelList = async ({ page, page_size } = {}) => {
    const response = await api({
      method: httpMethods.get,
      endPoint: apiEndpoints.riskLevel,
      needLoader: true,
      loader: loaderPath.addRiskLevel,
      params: { page, page_size },
      showToastOnError: true,
    });

    if (response?.status) {
      dispatch(addRiskLevel({ data: response?.data }));
    }
  };

  const callRiskLevelAPI = () => {
    getRiskLevelList({ page: currentPage, page_size: itemPerPage });
  };

  const openAddModal = () => {
    setModalMode("add");
    // Ensure default max_musharakah_weight is formatted for display
    const addDefaults = {
      ...riskLevelDefaultValues,
      max_musharakah_weight: formatNumber(
        riskLevelDefaultValues?.max_musharakah_weight,
      ),
    };
    setModalDefaultValues(addDefaults);
    reset(addDefaults);
    setModalOpen(true);
  };

  const openEditModal = (data) => {
    setModalMode("edit");
    // Format the editable value so the form shows the same formatted string
    const editDefaults = {
      ...data,
      max_musharakah_weight: formatNumber(data?.max_musharakah_weight),
    };
    setModalDefaultValues(editDefaults);
    reset(editDefaults);
    setModalOpen(true);
  };

  const hasAllRiskLevel =
    Array.isArray(riskLevelData) && riskLevelData.length >= 3;

  const onSubmit = async (formData) => {
    const {
      risk_level,
      equity_min,
      equity_max,
      penalty_amount,
      is_active,
      id,
      max_musharakah_weight,
    } = formData;

    if (equity_min === equity_max) {
      showToast("min-equity-not-equal-max-equity");
      return;
    }

    const apiData = {
      risk_level,
      penalty_amount,
      equity_min,
      equity_max,
      is_active: Boolean(is_active),
      max_musharakah_weight,
    };

    const normalizedRiskLevel = (risk_level || "").trim().toLowerCase();
    const isEdit = Boolean(id);

    const duplicateRiskLevel = (riskLevelData || []).some((item) => {
      if (!item?.risk_level) return false;
      const itemRiskLevel = (item.risk_level || "").trim().toLowerCase();
      if (isEdit) {
        return itemRiskLevel === normalizedRiskLevel && item.id !== id;
      }
      return itemRiskLevel === normalizedRiskLevel;
    });
    if (duplicateRiskLevel) {
      showToast("risk-level-already-exists");
      return;
    }

    const normalizedEquityMin = (equity_min || "").toString().trim();
    const duplicateEquityMin = (riskLevelData || []).some((item) => {
      if (item.equity_min === undefined || item.equity_min === null)
        return false;
      const itemEquityMin = (item.equity_min || "").toString().trim();
      if (isEdit) {
        return itemEquityMin === normalizedEquityMin && item.id !== id;
      }
      return itemEquityMin === normalizedEquityMin;
    });
    if (duplicateEquityMin) {
      showToast("min-equity-already-exists");
      return;
    }

    if (isEdit) setEditLoading(true);
    else setAddLoading(true);

    const response = await api({
      method: httpMethods[isEdit ? "patch" : "post"],
      endPoint: isEdit
        ? `${apiEndpoints.riskLevel}${id}/`
        : apiEndpoints.riskLevel,
      data: apiData,
      loader: loaderPath.addRiskLevel,
      needLoader: true,
      showToastMessage: true,
    });
    if (response?.status) {
      callRiskLevelAPI();
      setModalOpen(false);

      const updatedCount = isEdit ? count : (count || 0) + 1;
      const hasEnoughLevels = updatedCount >= 3;

      dispatch({
        type: "app/addAppData",
        payload: { name: "hasRiskLevels", data: hasEnoughLevels },
      });
      dispatch({
        type: "app/addAppData",
        payload: { name: "checkedRiskLevels", data: true },
      });
    }
    if (isEdit) setEditLoading(false);
    else setAddLoading(false);
  };

  const paginationProps = {
    currentPage,
    setItemPerPage,
    setCurrentPage,
    page: totalPage,
    rowsPerPage: itemPerPage,
  };

  const renderCell = (columnKey, data, _, value) => {
    switch (columnKey) {
      case "risk_level":
        return <SAStatusBadge status={data?.risk_level} />;

      case "penalty_amount":
        return <SATypography>{value}</SATypography>;

      case "equity_min":
        return <SATypography>{value}</SATypography>;

      case "equity_max":
        return <SATypography>{value}</SATypography>;

      case "status":
        return (
          <SAStatusBadge status={data?.is_active ? "ENABLED" : "DISABLED"} />
        );

      case "max_musharakah_weight":
        return <SATypography>{formatNumber(value)}</SATypography>;

      case "edit":
        return (
          <SAButton
            size="small"
            variant="outlined"
            onClick={() => openEditModal(data)}
            {...primaryActionStyle}
          >
            {t("edit-button")}
            {<Edit />}
          </SAButton>
        );

      default:
        return data[columnKey];
    }
  };

  return (
    <>
      <SACard>
        <SAStack
          pb={2}
          mb={1}
          mt={1}
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          borderBottom="1px solid gray.main 50"
        >
          <SATypography
            variant="body2"
            sx={{ fontWeight: "600", fontSize: "25px", marginLeft: "10px" }}
          >
            {t("risk-level")}
          </SATypography>

          {!hasAllRiskLevel && (
            <SAButton
              color="primary"
              variant="contained"
              onClick={openAddModal}
            >
              {t("add-risk-level")}
            </SAButton>
          )}
        </SAStack>
        <SABasicTable
          {...{
            renderCell,
            paginationProps,
            data: riskLevelData,
            loading: loadingState.addRiskLevel,
            columns: riskLevelTableColumns,
          }}
        />
      </SACard>

      <MUIModal
        open={modalOpen}
        maxWidth="xs"
        handleClose={() => setModalOpen(false)}
        title={t(
          modalMode === "add"
            ? "add-risk-level-title"
            : "edit-risk-level-title",
        )}
        content={
          <SABox
            py={4}
            px={{ xs: 2, md: 4 }}
            sx={{ backgroundColor: "#fff", borderRadius: 3, boxShadow: 1 }}
          >
            <SAForm
              key={modalDefaultValues?.id || modalMode}
              onSubmit={handleSubmit(onSubmit)}
            >
              <SAFormFields
                spacing={2}
                fields={riskLevelFields}
                register={register}
                formState={formState}
                control={control}
              />
              <SABox mt={4} textAlign="center">
                {modalMode === "add" ? (
                  <SAButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    loading={addLoading}
                  >
                    {t("add-risk-level")}
                  </SAButton>
                ) : (
                  <SAButton
                    type="submit"
                    disabled={!isDirty}
                    color="primary"
                    variant="contained"
                    loading={editLoading}
                  >
                    {t("save-changes")}
                  </SAButton>
                )}
              </SABox>
            </SAForm>
          </SABox>
        }
      />
    </>
  );
};

export default RiskLevel;
